import { useCallback } from 'react';
import { en, type MessageKey } from './en';
import { hi } from './hi';
import { ne } from './ne';
import { useSettings } from '../store/settings';

export type Lang = 'en' | 'ne' | 'hi';
const DICTS: Record<Lang, Partial<Record<MessageKey, string>>> = { en, ne, hi };
const LOCALES: Record<Lang, string> = { en: 'en-IN', ne: 'ne-NP', hi: 'hi-IN' };

export function translate(lang: Lang, key: MessageKey, vars?: Record<string, string | number>): string {
  const raw = DICTS[lang][key] ?? en[key] ?? key;
  if (!vars) return raw;
  return raw.replace(/\{(\w+)\}/g, (_, k: string) => (vars[k] !== undefined ? String(vars[k]) : `{${k}}`));
}

export function useT() {
  const lang = useSettings((s) => s.settings.language) as Lang;
  return useCallback((key: MessageKey, vars?: Record<string, string | number>) => translate(lang, key, vars), [lang]);
}

export function useFormat() {
  const lang = useSettings((s) => s.settings.language) as Lang;
  const locale = LOCALES[lang];
  return {
    num: (n: number) => new Intl.NumberFormat(locale).format(n),
    compact: (n: number) => new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 1 }).format(n),
    date: (d: string | number | Date) => new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(new Date(d)),
    time: (d: string | number | Date) => new Intl.DateTimeFormat(locale, { timeStyle: 'short' }).format(new Date(d)),
    relative: (d: string | number | Date) => {
      const diff = (new Date(d).getTime() - Date.now()) / 1000;
      const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
      const abs = Math.abs(diff);
      if (abs < 60) return rtf.format(Math.round(diff), 'second');
      if (abs < 3600) return rtf.format(Math.round(diff / 60), 'minute');
      if (abs < 86400) return rtf.format(Math.round(diff / 3600), 'hour');
      return rtf.format(Math.round(diff / 86400), 'day');
    },
  };
}

export type { MessageKey };
